/**
 * CORE-1200 — короткий код неудавшейся попытки авторизации.
 *
 * Код заводится ДО запроса (`beginAuthAttempt`), едет заголовком на бэк и
 * возвращается вызывающему на ошибке через `stampCorrelation`. Экрану остаётся
 * решить, показывать ли его человеку, и дать способ скопировать.
 *
 * Правило вынесено сюда, а не оставлено на экранах, по той же причине, по
 * которой вынесен `guestPublishGate`: инлайновые копии расходятся молча. Ровно
 * это здесь и произошло — блок с кодом жил только на экране входа, а человек,
 * пришедший по приглашению, регистрировался, и на его форме кода не было
 * вовсе (QA 06.09.2026, прод, Firefox: `NetworkError … (api.agapella.io)` без
 * кода и без кнопки «скопировать»).
 */
import { Platform } from 'react-native';

import { shouldReportApiError } from '../services/errorMessages';

/**
 * Показывать ли код этой ошибки.
 *
 * Тот же гвард, что решает «репортить ли в Sentry»: он отделяет НАШ сбой от
 * опечатки в пароле. Опечатка чинится не кодом, и код там только пугает.
 *
 * Сетевой отказ (`TypeError: NetworkError when attempting to fetch resource`)
 * статуса не несёт вовсе, поэтому гвард его пропускает — а это ровно тот
 * случай, ради которого задача и заводилась: запрос мог не дойти до бэка, и
 * отсутствие серверной записи по коду это и покажет.
 */
export function resolveAuthErrorCode(err: unknown): string | null {
  if (!shouldReportApiError(err)) return null;
  const code = (err as { correlationId?: unknown } | null | undefined)?.correlationId;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * Положить код в буфер обмена. Возвращает `true`, только если это правда
 * удалось: на вебе в небезопасном контексте `navigator.clipboard` отсутствует,
 * и обещать человеку успех там нельзя.
 *
 * Провал не оставляет его без кода — текст на экране `selectable`.
 */
export async function copyAuthErrorCode(code: string): Promise<boolean> {
  try {
    if (Platform.OS === 'web') {
      const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as
        | { clipboard?: { writeText?: (text: string) => Promise<void> } }
        | undefined;
      if (!nav?.clipboard?.writeText) return false;
      await nav.clipboard.writeText(code);
      return true;
    }
    const Clipboard = await import('expo-clipboard');
    await Clipboard.setStringAsync(code);
    return true;
  } catch {
    return false;
  }
}
